/**
 * Lightbox de imagen reutilizable (comprobantes de pago, etc.). A diferencia de un Modal de Polaris
 * con ancho fijo, se ADAPTA al tamaño de la imagen (centrada sobre un fondo oscuro, hasta 90vw/85vh).
 * Cierra con la X, clic en el fondo o Escape. Lo usan la lista de Pagos y la cronología.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/lib/polaris';
import { XIcon } from '@/lib/polaris-icons';

export function ImageZoomModal({ src, alt = 'Imagen', onClose }: { src: string | null; alt?: string; onClose: () => void }) {
  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);

  if (!src) return null;

  /*
   * 🚨 SE DIBUJA EN `document.body`, NO DONDE FUE LLAMADO.
   *
   *    El founder: «cuando hago zoom a la imagen, el z-index de la imagen sale por debajo de los
   *    botones y se ve feo».
   *
   * 🪤 Y NO SE ARREGLA SUBIENDO EL NÚMERO. `zIndex: 600` ya está por encima de toda la escala de
   *    Polaris (su capa más alta es 520). El problema es que un `z-index` sólo compite DENTRO de su
   *    contexto de apilado: si un ancestro creó uno —un modal, un `transform`, un `position` con
   *    z-index propio—, este 600 se compara contra los hermanos de ESE ancestro, no contra la
   *    página. Contra los botones de al lado, pierde.
   *
   *    El portal lo cuelga del `body`, donde no hay ancestro que lo encierre. Ahí el 600 vale de
   *    verdad. Subir el número habría tapado el síntoma en una pantalla y lo habría dejado igual en
   *    la siguiente.
   */
  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={alt} onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <button
        type="button" onClick={onClose} aria-label="Cerrar"
        style={{
          position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'rgba(255,255,255,0.9)',
        }}
      >
        <Icon source={XIcon} />
      </button>
      {/* La imagen define su propio tamaño (contain), centrada; el contenedor se adapta a ella. */}
      <img
        src={src} alt={alt} onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, background: '#fff', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}
      />
    </div>
  , document.body);
}
